import { useState, useCallback, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type { FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { WizardStepper } from '@/components/shared/WizardStepper'
import {
  AGENCY_TYPE_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  JOB_SEEKER_REGISTRATION_OPTIONS,
  UNIT_NAMING_TYPE_OPTIONS,
} from '@/constants/agencyRegistration'
import { useNotification } from '@refinedev/core'
import {
  CheckCircle2, Loader2, CheckCircle, XCircle, AlertCircle,
  Zap, Settings2, User, Users, ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type CreateMode = 'quick' | 'advanced'

const quickSchema = z.object({
  agency_name: z.string().min(2),
  contact_email: z.string().email(),
  phone: z.string().min(1),
  type: z.string().min(1),
  admin_user_mode: z.enum(['new', 'existing']),
  email: z.string().optional(),
  username: z.string().optional(),
  existing_user_id: z.string().optional(),
})

const advancedSchema = z.object({
  agency_name: z.string().min(2),
  type: z.string().min(1),
  industry: z.string().min(1),
  website: z.string().url().optional().or(z.literal('')),
  phone: z.string().min(1),
  company_size: z.string().min(1),
  contact_email: z.string().email(),
  contact_person: z.string().min(1),
  job_seeker_registration_method: z.string().min(1),
  unit_naming_type: z.string().min(1),
  admin_user_mode: z.enum(['new', 'existing']),
  email: z.string().optional(),
  username: z.string().optional(),
  existing_user_id: z.string().optional(),
  is_hr_manager: z.boolean().default(false),
  hr_manager_name: z.string().optional(),
  hr_manager_email: z.string().email().optional().or(z.literal('')),
})

type FormValues = z.infer<typeof advancedSchema>

const QUICK_STEPS = ['Agency Basics', 'Admin User']
const ADVANCED_STEPS = ['Agency Info', 'Admin User']

const QUICK_STEP_FIELDS: Array<(keyof FormValues)[]> = [
  ['agency_name', 'contact_email', 'phone', 'type'],
  ['admin_user_mode', 'email', 'username', 'existing_user_id'],
]
const ADVANCED_STEP_FIELDS: Array<(keyof FormValues)[]> = [
  ['agency_name', 'type', 'unit_naming_type', 'industry', 'phone', 'company_size',
   'contact_email', 'contact_person', 'job_seeker_registration_method', 'website'],
  ['admin_user_mode', 'email', 'username', 'existing_user_id'],
]

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken'

interface AvailabilityState {
  agency_name: AvailabilityStatus
  contact_email: AvailabilityStatus
  username: AvailabilityStatus
}

function AvailabilityBadge({ status }: { status: AvailabilityStatus }) {
  if (status === 'idle') return null
  if (status === 'checking') return (
    <span className="flex items-center gap-1 text-[10px] text-slate-400">
      <Loader2 size={10} className="animate-spin" /> Checking…
    </span>
  )
  if (status === 'available') return (
    <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
      <CheckCircle size={10} /> Available
    </span>
  )
  return (
    <span className="flex items-center gap-1 text-[10px] text-destructive">
      <XCircle size={10} /> Already taken
    </span>
  )
}

interface AdminUser { id: string; email: string; username: string }

function UserCombobox({ users, value, onChange }: {
  users: AdminUser[]
  value: string
  onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const filtered = users.filter(
    (u) => !q || u.email.toLowerCase().includes(q.toLowerCase()) || u.username?.toLowerCase().includes(q.toLowerCase())
  )
  const selected = users.find((u) => u.id === value)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full h-9 px-3 flex items-center justify-between gap-2 rounded-xl text-sm',
          'bg-slate-50 dark:bg-slate-700/50 border border-input dark:border-slate-600/60',
          'hover:bg-slate-100 dark:hover:bg-slate-700/70 focus:outline-none focus:ring-2 focus:ring-[#076698]/30',
          'transition-colors'
        )}
      >
        <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground'}>
          {selected ? `${selected.email} (${selected.username})` : 'Search and select a user…'}
        </span>
        <ChevronDown size={13} className="shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 top-10 left-0 right-0 glass-card p-2 space-y-1.5">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by email or username…"
            className="w-full h-8 px-3 rounded-lg text-xs form-field"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-1.5">No users found</p>
            ) : filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                className={cn(
                  'w-full text-left px-2 py-1.5 rounded-lg text-xs',
                  'hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors',
                  u.id === value && 'bg-[#076698]/10 text-[#076698] font-semibold'
                )}
                onClick={() => { onChange(u.id); setOpen(false); setQ('') }}
              >
                <span className="font-medium">{u.email}</span>
                {u.username && <span className="text-muted-foreground ml-1.5">@{u.username}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function CreateAgencyPage() {
  const [mode, setMode] = useState<CreateMode>('quick')
  const [step, setStep] = useState(0)
  const [success, setSuccess] = useState(false)
  const [conflictError, setConflictError] = useState('')
  const [availability, setAvailability] = useState<AvailabilityState>({
    agency_name: 'idle', contact_email: 'idle', username: 'idle',
  })
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const { open: openNotification } = useNotification()

  const steps = mode === 'quick' ? QUICK_STEPS : ADVANCED_STEPS
  const stepFields = mode === 'quick' ? QUICK_STEP_FIELDS : ADVANCED_STEP_FIELDS

  const {
    register, handleSubmit, watch, setValue, trigger, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(mode === 'quick' ? quickSchema : advancedSchema) as any,
    defaultValues: {
      is_hr_manager: false,
      type: '',
      company_size: '',
      job_seeker_registration_method: '',
      unit_naming_type: 'branch_department',
      admin_user_mode: 'new',
      existing_user_id: '',
    },
  })

  const isHrManager = watch('is_hr_manager')
  const typeValue = watch('type')
  const companySizeValue = watch('company_size')
  const jobSeekerRegValue = watch('job_seeker_registration_method')
  const adminUserMode = watch('admin_user_mode')

  useEffect(() => {
    if (adminUsers.length > 0) return
    setUsersLoading(true)
    flaskApi.get<AdminUser[]>('/users')
      .then((r) => setAdminUsers(r.data))
      .catch(() => { /* silent */ })
      .finally(() => setUsersLoading(false))
  }, [adminUsers.length])

  const checkAvailability = useCallback((field: keyof AvailabilityState, value: string) => {
    clearTimeout(debounceRef.current[field])
    if (!value || value.length < 2) {
      setAvailability((prev) => ({ ...prev, [field]: 'idle' }))
      return
    }
    setAvailability((prev) => ({ ...prev, [field]: 'checking' }))
    debounceRef.current[field] = setTimeout(async () => {
      try {
        const res = await flaskApi.get<{ available: boolean }>(
          `/check-availability?field=${field}&value=${encodeURIComponent(value)}`
        )
        setAvailability((prev) => ({ ...prev, [field]: res.data.available ? 'available' : 'taken' }))
      } catch {
        setAvailability((prev) => ({ ...prev, [field]: 'idle' }))
      }
    }, 500)
  }, [])

  const handleNext = async () => {
    const valid = await trigger(stepFields[step])
    const fields = stepFields[step]
    const hasTakenOnStep = (Object.keys(availability) as (keyof AvailabilityState)[]).some(
      (k) => (fields as string[]).includes(k) && availability[k] === 'taken'
    )
    if (valid && !hasTakenOnStep) {
      setStep((s) => s + 1)
    } else if (hasTakenOnStep) {
      openNotification?.({ type: 'error', message: 'Some fields are already taken', description: 'Please fix highlighted fields.' })
    }
  }

  const onSubmit = async (values: FormValues) => {
    const hasTaken = Object.values(availability).some((s) => s === 'taken')
    if (hasTaken) {
      openNotification?.({ type: 'error', message: 'Some fields are already taken' })
      return
    }

    if (values.admin_user_mode === 'new') {
      if (!values.email) {
        openNotification?.({ type: 'error', message: 'Admin email is required' })
        setStep(1)
        return
      }
      if (!values.username) {
        openNotification?.({ type: 'error', message: 'Admin username is required' })
        setStep(1)
        return
      }
    } else {
      if (!values.existing_user_id) {
        openNotification?.({ type: 'error', message: 'Please select an existing admin user' })
        setStep(1)
        return
      }
    }

    setConflictError('')
    try {
      await flaskApi.post('/agency', values)
      setSuccess(true)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { description?: string; message?: string } } })?.response?.status
      const description = (err as { response?: { data?: { description?: string; message?: string } } })?.response?.data?.description
        ?? (err as { response?: { data?: { description?: string; message?: string } } })?.response?.data?.message
        ?? 'Failed to create agency'

      if (status === 409) {
        setConflictError(description)
        setAvailability((prev) => ({ ...prev, agency_name: 'taken' }))
        setStep(0)
        openNotification?.({ type: 'error', message: 'Agency already exists', description })
        return
      }
      setConflictError(description)
      openNotification?.({ type: 'error', message: 'Failed to create agency', description })
    }
  }

  const onInvalid = (errs: FieldErrors<FormValues>) => {
    const errorFields = Object.keys(errs) as (keyof FormValues)[]
    for (let s = 0; s < stepFields.length; s++) {
      if (stepFields[s].some((f) => errorFields.includes(f))) {
        setStep(s)
        break
      }
    }
    openNotification?.({ type: 'error', message: 'Please fix all errors before submitting' })
  }

  const handleModeSwitch = (next: CreateMode) => {
    setMode(next)
    setStep(0)
    setConflictError('')
    setAvailability({ agency_name: 'idle', contact_email: 'idle', username: 'idle' })
    reset({ is_hr_manager: false, type: '', company_size: '', job_seeker_registration_method: '', unit_naming_type: 'branch_department', admin_user_mode: 'new', existing_user_id: '' })
  }

  if (success) {
    return (
      <div className="w-full max-w-xl mx-auto space-y-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Create Agency</h1>
        <div className="bento-card p-10 text-center space-y-4">
          <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
          <p className="text-lg font-bold text-slate-700 dark:text-slate-200">Agency created successfully!</p>
          <Button
            onClick={() => {
              setSuccess(false)
              setStep(0)
              setAvailability({ agency_name: 'idle', contact_email: 'idle', username: 'idle' })
              reset({ is_hr_manager: false, type: '', company_size: '', job_seeker_registration_method: '', unit_naming_type: 'branch_department', admin_user_mode: 'new', existing_user_id: '' })
            }}
            variant="outline"
          >
            Create Another
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Create Agency</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Set up a new agency and its initial admin user.</p>
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl">
          <button
            type="button"
            onClick={() => handleModeSwitch('quick')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
              mode === 'quick'
                ? 'bg-white dark:bg-slate-700 shadow text-[#076698] dark:text-sky-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            )}
          >
            <Zap size={11} />
            Quick Create
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('advanced')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
              mode === 'advanced'
                ? 'bg-white dark:bg-slate-700 shadow text-[#076698] dark:text-sky-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            )}
          >
            <Settings2 size={11} />
            Advanced
          </button>
        </div>
      </div>

      {mode === 'quick' && (
        <div className="flex items-start gap-2 px-4 py-2.5 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 text-xs text-sky-700 dark:text-sky-300">
          <Zap size={13} className="shrink-0 mt-0.5" />
          <span><strong>Quick Create</strong> — only mandatory fields. Switch to Advanced for full control.</span>
        </div>
      )}

      <div className="bento-card p-7 space-y-7">
        <div className="pb-6 border-b border-border/40">
          <WizardStepper steps={steps} current={step} />
        </div>

        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          {conflictError && (
            <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{conflictError}</span>
            </div>
          )}

          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Agency Name *</label>
                  <AvailabilityBadge status={availability.agency_name} />
                </div>
                <Input
                  {...register('agency_name', {
                    onChange: (e) => checkAvailability('agency_name', e.target.value),
                  })}
                  className={cn(availability.agency_name === 'taken' && 'border-destructive focus-visible:ring-destructive/50')}
                />
                {errors.agency_name && <p className="text-xs text-destructive">{errors.agency_name.message}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Type *</label>
                <Select
                  value={typeValue ?? ''}
                  onValueChange={(v) => setValue('type', v ?? '', { shouldValidate: true, shouldDirty: true })}
                >
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {AGENCY_TYPE_OPTIONS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.type && <p className="text-xs text-destructive">{errors.type.message}</p>}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Contact Email *</label>
                  <AvailabilityBadge status={availability.contact_email} />
                </div>
                <Input
                  {...register('contact_email', {
                    onChange: (e) => checkAvailability('contact_email', e.target.value),
                  })}
                  type="email"
                  className={cn(availability.contact_email === 'taken' && 'border-destructive focus-visible:ring-destructive/50')}
                />
                {errors.contact_email && <p className="text-xs text-destructive">{errors.contact_email.message}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone *</label>
                <Input {...register('phone')} />
                {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
              </div>

              {mode === 'advanced' && (
                <>
                  {[
                    { name: 'industry' as const, label: 'Industry' },
                    { name: 'website'  as const, label: 'Website' },
                    { name: 'contact_person' as const, label: 'Contact Person' },
                  ].map(({ name, label }) => (
                    <div key={name} className="space-y-1.5">
                      <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label} {name !== 'website' ? '*' : ''}</label>
                      <Input {...register(name)} />
                      {errors[name] && <p className="text-xs text-destructive">{errors[name]?.message}</p>}
                    </div>
                  ))}

                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Company Size *</label>
                    <Select
                      value={companySizeValue ?? ''}
                      onValueChange={(v) => setValue('company_size', v ?? '', { shouldValidate: true, shouldDirty: true })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZE_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.company_size && <p className="text-xs text-destructive">{errors.company_size.message}</p>}
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Job Seeker Registration *</label>
                    <Select
                      value={jobSeekerRegValue ?? ''}
                      onValueChange={(v) => setValue('job_seeker_registration_method', v ?? '', { shouldValidate: true, shouldDirty: true })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        {JOB_SEEKER_REGISTRATION_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.job_seeker_registration_method && (
                      <p className="text-xs text-destructive">{errors.job_seeker_registration_method.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Unit Naming Type *</label>
                    <Select
                      value={watch('unit_naming_type') ?? 'branch_department'}
                      onValueChange={(v) => setValue('unit_naming_type', v ?? 'branch_department', { shouldValidate: true, shouldDirty: true })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select naming type" /></SelectTrigger>
                      <SelectContent>
                        {UNIT_NAMING_TYPE_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.unit_naming_type && (
                      <p className="text-xs text-destructive">{errors.unit_naming_type.message}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-1.5 p-1 self-start bg-slate-100 dark:bg-slate-800/60 rounded-xl w-fit">
                {([
                  { val: 'new',      icon: User,  label: 'Create New User' },
                  { val: 'existing', icon: Users, label: 'Use Existing User' },
                ] as const).map(({ val, icon: Icon, label }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setValue('admin_user_mode', val, { shouldValidate: false })}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
                      adminUserMode === val
                        ? 'bg-white dark:bg-slate-700 shadow text-[#076698] dark:text-sky-400'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                    )}
                  >
                    <Icon size={11} />
                    {label}
                  </button>
                ))}
              </div>

              {adminUserMode === 'new' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <p className="sm:col-span-2 text-sm text-slate-500 dark:text-slate-400">
                    A new user account will be created and assigned as the agency admin.
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Admin Email *</label>
                    <Input {...register('email')} type="email" />
                    {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Admin Username *</label>
                      <AvailabilityBadge status={availability.username} />
                    </div>
                    <Input
                      {...register('username', {
                        onChange: (e) => checkAvailability('username', e.target.value),
                      })}
                      className={cn(availability.username === 'taken' && 'border-destructive focus-visible:ring-destructive/50')}
                    />
                    {errors.username && <p className="text-xs text-destructive">{errors.username.message}</p>}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Select an existing platform user to become the admin of this agency.
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Select Admin User *</label>
                    {usersLoading ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground p-2">
                        <Loader2 size={13} className="animate-spin" /> Loading users…
                      </div>
                    ) : (
                      <UserCombobox
                        users={adminUsers}
                        value={watch('existing_user_id') ?? ''}
                        onChange={(id) => setValue('existing_user_id', id, { shouldValidate: true })}
                      />
                    )}
                    {errors.existing_user_id && <p className="text-xs text-destructive">{errors.existing_user_id.message}</p>}
                  </div>
                </div>
              )}

              {mode === 'advanced' && (
                <div className="pt-4 border-t border-border/40 space-y-4">
                  <p className="text-xs font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">HR Manager</p>
                  <label className="flex items-center gap-2.5 text-sm cursor-pointer font-medium text-slate-700 dark:text-slate-200 select-none">
                    <input type="checkbox" {...register('is_hr_manager')} className="rounded accent-[#076698] w-4 h-4" />
                    Has a separate HR Manager account
                  </label>
                  {isHrManager && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-5 border-l-2 border-[#076698]">
                      <div className="space-y-1.5">
                        <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">HR Manager Name *</label>
                        <Input {...register('hr_manager_name')} />
                        {errors.hr_manager_name && <p className="text-xs text-destructive">{errors.hr_manager_name.message}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">HR Manager Email *</label>
                        <Input {...register('hr_manager_email')} type="email" />
                        {errors.hr_manager_email && <p className="text-xs text-destructive">{errors.hr_manager_email.message}</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mt-7 pt-5 border-t border-border/40">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 0 || isSubmitting}
            >
              ← Back
            </Button>
            <span className="text-[10px] font-bold text-slate-400 text-center uppercase tracking-widest">
              Step {step + 1} of {steps.length}
            </span>
            {step < steps.length - 1 ? (
              <Button
                type="button"
                className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                onClick={handleNext}
              >
                Continue →
              </Button>
            ) : (
              <Button
                type="submit"
                className="min-w-[140px] bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={15} className="animate-spin" />
                    Creating…
                  </span>
                ) : 'Create Agency'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
